import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Includes/Sidebar';
import GeneralService from '../../services/general.service';
import Footer from '../Includes/Footer';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

export default function EditDoctor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [removeExistingImageFlag, setRemoveExistingImageFlag] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Base URL for images
    const BASE_IMAGE_URL = process.env.REACT_APP_API_BASE_URL || 'https://azmrehabcentre.pk/api/public';

    const { values, errors, handleBlur, handleChange, handleSubmit, setFieldValue, setValues } = useFormik({
        initialValues: {
            name: "",
            designation: "",
            specialization: "",
            degrees: "",
            gender: "",
            image: null
        },
        validate: (values) => {
            const errors = {};
            
            // Name validation
            if (!values.name || !values.name.trim()) {
                errors.name = "Name is required";
            } else if (values.name.length > 255) {
                errors.name = "Name must be less than 255 characters";
            }
            
            // Designation validation
            if (values.designation && values.designation.length > 255) {
                errors.designation = "Designation must be less than 255 characters";
            }
            
            // Specialization validation
            if (values.specialization && values.specialization.length > 255) {
                errors.specialization = "Specialization must be less than 255 characters";
            }
            
            // Degrees validation
            if (values.degrees && values.degrees.length > 255) {
                errors.degrees = "Degrees must be less than 255 characters";
            }
            
            // Gender validation
            if (values.gender && !['male', 'female', 'other'].includes(values.gender)) {
                errors.gender = "Please select a valid gender";
            }
            
            // Image validation (only if new image is selected)
            if (values.image && typeof values.image === 'object') {
                const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
                const maxSize = 2048 * 1024; // 2MB in bytes
                
                if (!allowedTypes.includes(values.image.type)) {
                    errors.image = "Only JPG, JPEG, PNG, and WEBP files are allowed";
                } else if (values.image.size > maxSize) {
                    errors.image = "Image size must be less than 2MB";
                }
            }
            
            return errors;
        },
        onSubmit: async (values, action) => {
            setIsLoading(true);
            try {
                console.log('Form values before submission:', values);
                console.log('Remove existing image flag:', removeExistingImageFlag);
                
                // Create FormData object
                const formData = new FormData();
                
                // Add method spoofing for Laravel PUT request
                formData.append('_method', 'PUT');
                
                // Always add name (required field) - send even if empty for proper validation
                formData.append('name', values.name || '');
                
                // Add other fields - send all fields to maintain consistency
                formData.append('designation', values.designation || '');
                formData.append('specialization', values.specialization || '');
                formData.append('degrees', values.degrees || '');
                formData.append('gender', values.gender || '');
                
                // Add image if selected
                if (values.image && typeof values.image === 'object') {
                    formData.append('image', values.image);
                }
                
                // Add remove existing image flag
                if (removeExistingImageFlag) {
                    formData.append('removeExistingImage', 'true');
                }

                // Log FormData contents for debugging
                console.log('FormData contents:');
                for (let [key, value] of formData.entries()) {
                    console.log(key, value);
                }

                // Update Doctor API Call - pass FormData directly
                const response = await GeneralService.updateDoctorWithFormData(id, formData);

                toast.success("Doctor updated successfully");
                
                // Navigate back to doctors list after successful update
                setTimeout(() => {
                    navigate(-1);
                }, 1500);
                
            } catch (err) {
                console.error("Error updating doctor:", err);
                console.error("Error response:", err.response?.data);
                const errorMessage = err.response?.data?.message || 
                               err.response?.data?.error || 
                               "Error updating doctor";
                toast.error(errorMessage);
            }
            setIsLoading(false);
        }
    });

    // Fetch doctor data for editing
    const fetchDoctorData = async () => {
        try {
            setIsDataLoading(true);
            console.log("Fetching doctor data for ID:", id);
            
            const response = await GeneralService.getDoctorById(id);
            console.log("Doctor data response:", response);
            
            // Handle different response structures - check for both patterns
            const doctorData = response.data?.data || response.data;
            
            if (!doctorData) {
                throw new Error("No doctor data received");
            }
            
            // Set form values with existing data
            setValues({
                name: doctorData.name || "",
                designation: doctorData.designation || "",
                specialization: doctorData.specialization || "",
                degrees: doctorData.degrees || "",
                gender: doctorData.gender || "",
                image: null // Don't set existing image as file object
            });

            // Set existing image for preview
            if (doctorData.image) {
                const imageUrl = doctorData.image.startsWith('http') 
                    ? doctorData.image 
                    : `${BASE_IMAGE_URL}/${doctorData.image.replace(/^\/+/, '')}`;
                setExistingImage(imageUrl);
            }

        } catch (error) {
            console.error("Error fetching doctor data:", error);
            console.error("Error response:", error.response?.data);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               "Failed to load doctor data";
            toast.error(errorMessage);
            
            // Navigate back after a delay to show the error message
            setTimeout(() => {
                navigate(-1);
            }, 2000);
        } finally {
            setIsDataLoading(false);
        }
    };

    // Simplified and fixed delete function
    const handleDeleteDoctor = async () => {
        if (!id) {
            toast.error("Doctor ID is missing");
            return;
        }

        setIsDeleting(true);
        
        try {
            console.log("Deleting doctor with ID:", id);
            
            // Simple delete call - let the service handle the implementation
            const response = await GeneralService.deleteDoctor(id);
            
            console.log("Delete successful:", response);
            toast.success("Doctor deleted successfully");
            
            // Close modal and navigate
            setShowDeleteModal(false);
            setTimeout(() => {
                navigate(-1);
            }, 1500);
            
        } catch (error) {
            console.error("Delete error:", error);
            
            // Better error handling
            let errorMessage = "Failed to delete doctor";
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 404) {
                errorMessage = "Doctor not found or already deleted";
            } else if (error.response?.status === 403) {
                errorMessage = "You don't have permission to delete this doctor";
            } else if (error.response?.status === 409) {
                errorMessage = "Cannot delete doctor - there may be related records";
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    // Show delete confirmation modal
    const showDeleteConfirmation = () => {
        setShowDeleteModal(true);
    };

    // Hide delete confirmation modal
    const hideDeleteConfirmation = () => {
        setShowDeleteModal(false);
    };

    useEffect(() => {
        if (id) {
            fetchDoctorData();
        } else {
            toast.error("No doctor ID provided");
            navigate(-1);
        }
    }, [id, navigate]);

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFieldValue("image", file);
            
            // Create image preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFieldValue("image", null);
            setImagePreview(null);
        }
    };

    // Remove selected image
    const removeImage = () => {
        setFieldValue("image", null);
        setImagePreview(null);
        // Reset file input
        const fileInput = document.getElementById('image');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Remove existing image
    const removeExistingImage = () => {
        setExistingImage(null);
        setRemoveExistingImageFlag(true);
    };

    if (isDataLoading) {
        return (
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading doctor data...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0">Edit Doctor</h6>
                            <div className="d-flex gap-2">
                                <button 
                                    className="btn btn-danger"
                                    onClick={showDeleteConfirmation}
                                    type="button"
                                    disabled={isDeleting || isLoading}
                                >
                                    {isDeleting ? "Deleting..." : "Delete Doctor"}
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => navigate(-1)}
                                    type="button"
                                    disabled={isDeleting || isLoading}
                                >
                                    Back to List
                                </button>
                            </div>
                        </div>
                        <form onSubmit={handleSubmit} noValidate>
                            <div className='row'>
                                {/* Name Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            id="name"
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter doctor's name"
                                            required
                                        />
                                        <label htmlFor="name">Name *</label>
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>
                                </div>

                                {/* Designation Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.designation ? 'is-invalid' : ''}`}
                                            id="designation"
                                            name="designation"
                                            value={values.designation}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter designation"
                                        />
                                        <label htmlFor="designation">Designation</label>
                                        {errors.designation && <div className="invalid-feedback">{errors.designation}</div>}
                                    </div>
                                </div>

                                {/* Specialization Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.specialization ? 'is-invalid' : ''}`}
                                            id="specialization"
                                            name="specialization"
                                            value={values.specialization}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter specialization"
                                        />
                                        <label htmlFor="specialization">Specialization</label>
                                        {errors.specialization && <div className="invalid-feedback">{errors.specialization}</div>}
                                    </div>
                                </div>

                                {/* Degrees Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.degrees ? 'is-invalid' : ''}`}
                                            id="degrees"
                                            name="degrees"
                                            value={values.degrees}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter degrees (e.g., MBBS, MD)"
                                        />
                                        <label htmlFor="degrees">Degrees</label>
                                        {errors.degrees && <div className="invalid-feedback">{errors.degrees}</div>}
                                    </div>
                                </div>

                                {/* Gender Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <select
                                            className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
                                            id="gender"
                                            name="gender"
                                            value={values.gender}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <label htmlFor="gender">Gender</label>
                                        {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                                    </div>
                                </div>

                                {/* Image Upload Field */}
                                <div className='col-md-6'>
                                    <div className="mb-3">
                                        <label htmlFor="image" className="form-label">Doctor Image</label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                                            id="image"
                                            name="image"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleImageChange}
                                        />
                                        <div className="form-text">Max size: 2MB. Allowed formats: JPG, JPEG, PNG, WEBP</div>
                                        {errors.image && <div className="invalid-feedback">{errors.image}</div>}
                                    </div>
                                </div>

                                {/* Existing Image Preview */}
                                {existingImage && !imagePreview && (
                                    <div className='col-md-12'>
                                        <div className="mb-3">
                                            <label className="form-label">Current Image</label>
                                            <div className="image-preview-container">
                                                <img
                                                    src={existingImage}
                                                    alt="Current Doctor"
                                                    className="image-preview"
                                                    onError={(e) => {
                                                        console.error('Image failed to load:', existingImage);
                                                        e.target.style.display = 'none';
                                                        setExistingImage(null);
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger remove-image-btn"
                                                    onClick={removeExistingImage}
                                                    title="Remove current image"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* New Image Preview */}
                                {imagePreview && (
                                    <div className='col-md-12'>
                                        <div className="mb-3">
                                            <label className="form-label">New Image Preview</label>
                                            <div className="image-preview-container">
                                                <img
                                                    src={imagePreview}
                                                    alt="New Doctor Preview"
                                                    className="image-preview"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger remove-image-btn"
                                                    onClick={removeImage}
                                                >
                                                    <i className="fas fa-times"></i> Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div className="col-md-12 d-flex">
                                    <button
                                        className="btn btn-submit"
                                        type="submit"
                                        disabled={isLoading || isDeleting}
                                    >
                                        {isLoading ? "Updating..." : "Update Doctor"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <Footer />
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal fade show" style={{display: 'block'}} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={hideDeleteConfirmation}
                                    disabled={isDeleting}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this doctor?</p>
                                <p><strong>Name:</strong> {values.name}</p>
                                <p className="text-danger"><strong>This action cannot be undone.</strong></p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={hideDeleteConfirmation}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={handleDeleteDoctor}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? "Deleting..." : "Delete Doctor"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Backdrop */}
            {showDeleteModal && (
                <div 
                    className="modal-backdrop fade show"
                    onClick={hideDeleteConfirmation}
                ></div>
            )}

            <style jsx>{`
                .btn-submit {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    padding: 14px 28px;
                    font-size: 16px;
                    font-weight: 600;
                    text-transform: uppercase;
                    border-radius: 30px;
                    transition: background-color 0.3s, border-color 0.3s;
                    display: inline-block;
                    margin-top: 10px;
                    color: #fff;
                }

                .btn-submit:hover {
                    background-color: #d43f00;
                    border-color: #d43f00;
                    color: #fff;
                }

                .btn-submit:focus {
                    outline: none;
                }

                .image-preview-container {
                    position: relative;
                    display: inline-block;
                }

                .image-preview {
                    max-width: 200px;
                    max-height: 200px;
                    border-radius: 8px;
                    border: 2px solid #dee2e6;
                    object-fit: cover;
                }

                .remove-image-btn {
                    position: absolute;
                    top: -10px;
                    right: -10px;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                }

                .invalid-feedback {
                    display: block;
                }

                .modal {
                    z-index: 1050;
                }

                .modal-backdrop {
                    z-index: 1040;
                }
            `}</style>
        </>
    );
}